import {
  BarChart3,
  ChevronDown,
  Database,
  FileCheck2,
  HardDrive,
  House,
  LogOut,
  Menu,
  Search,
  Settings,
  Sparkles,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { Brand } from './Brand';
import { TableScrollToTop } from './TableScrollToTop';

const primaryNavigation = [
  { to: '/app', label: 'Home', icon: House, end: true },
  { to: '/app/sorgenti', label: 'Drive', icon: HardDrive },
  { to: '/app/elaborazioni', label: 'Source', icon: Database },
  { to: '/app/validazione', label: 'Verifica', icon: FileCheck2 },
  { to: '/app/output', label: 'Report', icon: BarChart3 },
];

const adminNavigation = [
  { to: '/app/impostazioni', label: 'Impostazioni', icon: Settings },
];

const roleLabels = {
  superadmin: 'Superadmin',
  admin: 'Admin',
  utente: 'Utente',
} as const;

export function AppShell({ children }: { children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  useEffect(() => {
    const closeProfile = (event: MouseEvent) => {
      if (!profileRef.current?.contains(event.target as Node)) setProfileOpen(false);
    };
    window.addEventListener('mousedown', closeProfile);
    return () => window.removeEventListener('mousedown', closeProfile);
  }, []);

  const handleSignOut = async () => {
    setProfileOpen(false);
    await signOut();
    navigate('/accesso', { replace: true });
  };

  const initials = (user?.displayName || user?.email || 'J')
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  const renderNav = (items: typeof primaryNavigation) => items.map(({ to, label, icon: Icon, end }) => (
    <NavLink
      key={to}
      to={to}
      end={end}
      onClick={() => setMobileOpen(false)}
      className={({ isActive }) => (isActive ? 'active' : undefined)}
    >
      <Icon size={18} strokeWidth={1.8} />
      <span>{label}</span>
    </NavLink>
  ));

  return (
    <div className="app-shell">
      <button
        className={`sidebar-scrim${mobileOpen ? ' visible' : ''}`}
        type="button"
        aria-label="Chiudi navigazione"
        onClick={() => setMobileOpen(false)}
      />
      <aside className={`app-sidebar${mobileOpen ? ' open' : ''}`}>
        <div className="sidebar-header">
          <Brand inverse />
          <button type="button" className="sidebar-close" aria-label="Chiudi menu" onClick={() => setMobileOpen(false)}>
            <X size={20} />
          </button>
        </div>

        <nav className="app-nav" aria-label="Navigazione principale">
          <p>Processo</p>
          {renderNav(primaryNavigation)}
        </nav>

        {user?.role !== 'utente' && (
          <div className="sidebar-footer">
            {renderNav(adminNavigation)}
          </div>
        )}
      </aside>

      <div className="app-main">
        <header className="app-topbar">
          <div className="topbar-left">
            <button className="menu-button" type="button" onClick={() => setMobileOpen(true)} aria-label="Apri menu">
              <Menu size={20} />
            </button>
            <label className="global-search">
              <Search size={17} />
              <input type="search" placeholder="Cerca dati…" aria-label="Cerca dati" />
              <kbd>⌘ K</kbd>
            </label>
          </div>

          <div className="topbar-actions">
            {user?.demo && <span className="demo-chip"><Sparkles size={13} /> Demo locale</span>}
            <div className="profile-menu" ref={profileRef}>
              <button
                className="profile-trigger"
                type="button"
                aria-haspopup="menu"
                aria-expanded={profileOpen}
                onClick={() => setProfileOpen((open) => !open)}
              >
                <span className="avatar">{initials}</span>
                <span className="profile-copy">
                  <strong>{user?.displayName}</strong>
                  <small>{user ? roleLabels[user.role] : ''}</small>
                </span>
                <ChevronDown size={15} />
              </button>
              {profileOpen && (
                <div className="profile-popover" role="menu">
                  <div className="profile-popover-head">
                    <span className="avatar avatar-large">{initials}</span>
                    <span><strong>{user?.displayName}</strong><small>{user?.email}</small></span>
                  </div>
                  <span className="role-badge">{user ? roleLabels[user.role] : ''}</span>
                  <div className="profile-popover-actions">
                    <button type="button" role="menuitem" onClick={() => void handleSignOut()}>
                      <LogOut size={16} /> Esci da Talos
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
        {children}
      </div>
      <TableScrollToTop />
    </div>
  );
}
